import { gql, useQuery } from '@apollo/client';
import { getLocalTimeZone, today } from '@internationalized/date';
import * as React from 'react';

import {
  toBalanceHistoryBounds,
  toDailySeries,
  valueOverTimeView,
  type ValueOverTimeView,
} from '../../utils/account/balanceHistory';

import type {
  GetFinancialAccountBalanceHistoryQuery,
  GetFinancialAccountBalanceHistoryQueryVariables,
} from './__generated__/useFinancialAccountBalanceHistory.gql';
import type { CalendarDateRange } from '../../components/DateRangePicker';

const GET_FINANCIAL_ACCOUNT_BALANCE_HISTORY = gql`
  query GetFinancialAccountBalanceHistory(
    $id: ID!
    $filters: FinancialAccountBalanceFilters!
    $carryInDate: DateTimeISO!
  ) {
    getFinancialAccount(id: $id) {
      id
      historicalBalances(filters: $filters) {
        id
        balance
        timestamp
      }
      balanceAt(date: $carryInDate) {
        id
        balance
        timestamp
      }
    }
  }
`;

/**
 * An account's recorded values over a date range, as the state a value-over-time chart draws.
 *
 * Days are the viewer's: the browser zone sets both the range's instants and each snapshot's day.
 * The read also fetches the latest value before the range, which is what tells a new account
 * from a narrow range.
 *
 * @param accountId `null` skips the read.
 * @returns `view` is null until a result arrives, on `error`, when skipped, and when the account
 *   is not found — never an empty state, which would claim the account has no values. A cached
 *   `view` stays available while `loading` refetches it.
 */
export const useFinancialAccountBalanceHistory = (accountId: string | null, dateRange: CalendarDateRange) => {
  const zone = getLocalTimeZone();
  const { startDate, endDate, carryInDate } = toBalanceHistoryBounds(dateRange, zone);

  const { data, loading, error } = useQuery<
    GetFinancialAccountBalanceHistoryQuery,
    GetFinancialAccountBalanceHistoryQueryVariables
  >(GET_FINANCIAL_ACCOUNT_BALANCE_HISTORY, {
    variables: accountId
      ? {
          id: accountId,
          filters: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
          carryInDate: carryInDate.toISOString(),
        }
      : undefined,
    skip: accountId === null,
    fetchPolicy: 'cache-and-network',
  });

  const account = data?.getFinancialAccount ?? null;

  const view = React.useMemo<ValueOverTimeView | null>(() => {
    if (!account || error) {
      return null;
    }

    const snapshots = account.historicalBalances.map(({ balance, timestamp }) => ({
      balance: parseFloat(balance),
      timestamp,
    }));

    return valueOverTimeView({
      series: toDailySeries(snapshots, zone),
      carryIn: account.balanceAt ? parseFloat(account.balanceAt.balance) : null,
      dateRange,
      today: today(zone),
    });
  }, [account, error, dateRange, zone]);

  return { view, loading, error };
};
